import { useCallback, useRef, useState } from "react";

export type RowWrites = {
	/** Rows whose write of this kind is in flight, so each row can show its own spinner. */
	ids: string[];
	/** Locks one row. Returns false when that row already has a write of this kind in flight. */
	begin: (id: string) => boolean;
	/** Releases the row once the write settles, reporting a failure through `onError`. */
	track: (id: string, write: Promise<unknown>, onError: (error: unknown) => void) => void;
	reset: () => void;
};

/**
 * Tracks Active Rules rows with one kind of write in flight. Rows other than the one being written
 * stay clickable, so several rows can be in flight at once. The write is followed through its own
 * promise rather than through mutate callbacks, because a mutation hook follows only its newest
 * call: a second row would otherwise drop the first row's callbacks and leave it locked with its
 * error unreported.
 */
export function useRowWrites(): RowWrites {
	const inFlightRef = useRef<Set<string>>(new Set());
	const [ids, setIds] = useState<string[]>([]);

	const begin = useCallback((id: string): boolean => {
		if (inFlightRef.current.has(id)) {
			return false;
		}

		inFlightRef.current.add(id);
		setIds((current) => [...current, id]);
		return true;
	}, []);

	const track = useCallback((id: string, write: Promise<unknown>, onError: (error: unknown) => void): void => {
		void write.catch(onError).finally(() => {
			inFlightRef.current.delete(id);
			setIds((current) => current.filter((rowId) => rowId !== id));
		});
	}, []);

	const reset = useCallback((): void => {
		inFlightRef.current.clear();
		setIds([]);
	}, []);

	return { ids, begin, track, reset };
}
